// Scan rendezvous bridge (engine seam between the sticker adapter and the
// in-scene camera scanner).
//
// The adapter side calls begin_scan() to request a scan; the bridge invokes the
// `activate` callback (which brings the Video scanner up in the active scene)
// and returns a promise. The scene side (Video SceneObject) calls
// report_scanned/report_cancelled/report_error to settle it. The bridge always
// invokes `deactivate` exactly once on settle so the camera/subscriptions are
// torn down on every outcome.
//
// A single scan may be in flight at a time: begin_scan while one is active
// rejects with `scan_failed` (duplicate prevention). This module is
// dependency-free (only sticker public types) so it is unit-testable without a
// browser, camera, or GPU.

import { StickerError, type StickerCode } from '../sticker/sticker_types';

type Deactivate = () => void;

interface ActiveScan {
  resolve: (code: StickerCode) => void;
  reject: (err: StickerError) => void;
  deactivate: Deactivate;
  signal?: AbortSignal;
  onAbort?: () => void;
}

export class ScanBridge {
  private active: ActiveScan | null = null;

  /** True while a scan is in flight. */
  is_scanning(): boolean {
    return this.active !== null;
  }

  /**
   * Request a scan. Invokes `activate` to bring the scanner up, then resolves
   * with the stabilized code (or rejects cancelled/error). `deactivate` is
   * invoked exactly once when the scan settles.
   */
  begin_scan(
    signal: AbortSignal | undefined,
    activate: () => void,
    deactivate: Deactivate
  ): Promise<StickerCode> {
    if (this.active) {
      return Promise.reject(
        new StickerError('scan_failed', 'A scan is already in progress')
      );
    }
    if (signal?.aborted) {
      return Promise.reject(new StickerError('cancelled', 'Scan cancelled'));
    }

    return new Promise<StickerCode>((resolve, reject) => {
      const onAbort = () => this.report_cancelled();
      this.active = { resolve, reject, deactivate, signal, onAbort };
      signal?.addEventListener('abort', onAbort, { once: true });
      // Bring the scanner up last, after state is armed, so a synchronous
      // report during activation is handled correctly.
      activate();
    });
  }

  /** Settle the in-flight scan with a stabilized code. No-op if idle. */
  report_scanned(code: StickerCode): void {
    const scan = this.take();
    if (!scan) return;
    scan.resolve(code);
  }

  /** Settle the in-flight scan as cancelled. No-op if idle. */
  report_cancelled(): void {
    const scan = this.take();
    if (!scan) return;
    scan.reject(new StickerError('cancelled', 'Scan cancelled'));
  }

  /** Settle the in-flight scan as a typed error. No-op if idle. */
  report_error(error: StickerError): void {
    const scan = this.take();
    if (!scan) return;
    scan.reject(error);
  }

  private take(): ActiveScan | null {
    const scan = this.active;
    if (!scan) return null;
    this.active = null;
    if (scan.signal && scan.onAbort) {
      scan.signal.removeEventListener('abort', scan.onAbort);
    }
    // Tear the scanner down before settling so listeners observing the promise
    // never see a live scanner.
    scan.deactivate();
    return scan;
  }
}

// Engine-wide singleton shared by the adapter's controller and the Video scanner.
export const scanBridge = new ScanBridge();
